/**
 * Redacts secrets from telemetry payloads via key denylist + value patterns.
 * Mutates input in place. See PLT-102416.
 */

export const REDACTED = '<REDACTED>';

const MAX_DEPTH = 32;
const MIN_SECRET_LENGTH = 16;

const SENSITIVE_KEY_SUBSTRINGS = [
    'authorization',
    'wwwauthenticate',
    'cookie',
    'csrf',
    'xsrf',
    'token',
    'apikey',
    'password',
    'pwd',
    'secret',
    'credential',
    'privatekey',
    'signature',
    'pin',
    'otp',
];

const VALUE_PATTERNS: Array<{ regex: RegExp; replacement: string }> = [
    // Keep the "Bearer" prefix as a hint about what kind of secret was scrubbed.
    {
        regex: /Bearer\s+[A-Za-z0-9._~+/=-]+/gi,
        replacement: `Bearer ${REDACTED}`,
    },
    // Raw JWT — three dot-separated base64url segments starting with `eyJ`.
    {
        regex: /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{8,}/g,
        replacement: REDACTED,
    },
    {
        regex: /\b(AccountKey|SharedAccessKey|SharedAccessSignature)=([^;\s"'<>]+)/gi,
        replacement: '$1=' + REDACTED,
    },
];

function normalizeKey(key: string): string {
    return key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

function isSensitiveKey(key: string): boolean {
    const normalized = normalizeKey(key);
    return SENSITIVE_KEY_SUBSTRINGS.some(s => normalized.includes(s));
}

export function scrubString(value: string): string {
    if (value.length < MIN_SECRET_LENGTH) {
        return value;
    }
    let out = value;
    for (const {
        regex, replacement,
    } of VALUE_PATTERNS) {
        out = out.replace(regex, replacement);
    }
    return out;
}

// Recurse into Error subclasses too — Axios errors are passed in via `properties.error`.
function isPlainContainer(value: unknown): value is Record<string, unknown> | unknown[] {
    if (value == null || typeof value !== 'object') {
        return false;
    }
    if (Array.isArray(value) || value instanceof Error) {
        return true;
    }
    const proto = Object.getPrototypeOf(value);
    return proto == null || proto === Object.prototype;
}

export function scrubSecrets<T extends object>(input: T, depth = 0, seen: WeakSet<object> = new WeakSet()): T {
    if (!isPlainContainer(input) || depth >= MAX_DEPTH || seen.has(input)) {
        return input;
    }
    seen.add(input);

    if (Array.isArray(input)) {
        for (let i = 0; i < input.length; i++) {
            const v = input[i];
            if (typeof v === 'string') {
                input[i] = scrubString(v);
            } else if (isPlainContainer(v)) {
                scrubSecrets(v, depth + 1, seen);
            }
        }
        return input;
    }

    const obj = input as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
        if (isSensitiveKey(key)) {
            obj[key] = REDACTED;
            continue;
        }
        const v = obj[key];
        if (typeof v === 'string') {
            obj[key] = scrubString(v);
        } else if (isPlainContainer(v)) {
            scrubSecrets(v, depth + 1, seen);
        }
    }
    return input;
}
